// Anschrift und Kontaktwege "über <Organisation>" (P7 A, Michael E2; Leseweg
// GET /parties/{id}/via-organization): was eine Person von ihrer Organisation
// übernimmt, wenn sie selbst keine nicht private Angabe derselben Art hat.
//
// NUR ANZEIGE. Ein Fehler des Abrufs verdeckt nie die eigenen Daten: jeder
// Ausgang ausser einer gueltigen Antwort mit Organisation ist "nichts
// übernommen" -- ohne Hinweis, denn eine unsichtbare Organisation darf sich
// nicht verraten (CM-SEC-003).

import { readResource, type SendLike } from './command-client.ts';
import { usageLabel } from './parties.ts';

export interface ViaOrganizationValue {
  readonly value: string;
  /** Die Verwendung in Worten: "Zentrale" oder "geschäftlich". */
  readonly usage: string;
}

export interface ViaOrganization {
  readonly organizationPartyId: string;
  readonly organizationName: string;
  /** Weitere laufende Beschäftigungen; sie zählen nur für "+ N weitere". */
  readonly additionalEmploymentCount: number;
  readonly postalAddress?: { readonly lines: readonly string[]; readonly countryCode: string };
  readonly emailAddress?: ViaOrganizationValue;
  readonly phoneNumber?: ViaOrganizationValue;
}

const ASRECORD = (value: unknown): Record<string, unknown> | undefined =>
  value !== null && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : undefined;

/** Nur die beiden Verwendungen, die der Leseweg übernimmt; alles andere gilt als nicht übernommen. */
const UEBERNOMMEN = new Set(['reception', 'business']);

function valueOf(raw: unknown): ViaOrganizationValue | undefined {
  const row = ASRECORD(raw);
  if (row === undefined || typeof row.value !== 'string' || row.value === '' || typeof row.usageCode !== 'string' || !UEBERNOMMEN.has(row.usageCode)) {
    return undefined;
  }
  return { value: row.value, usage: usageLabel(row.usageCode) };
}

function addressOf(raw: unknown): ViaOrganization['postalAddress'] {
  const row = ASRECORD(raw);
  if (row === undefined || !Array.isArray(row.lines) || typeof row.countryCode !== 'string') return undefined;
  const lines = row.lines.filter((line): line is string => typeof line === 'string' && line !== '');
  return lines.length === 0 ? undefined : { lines, countryCode: row.countryCode };
}

/** Liest die Antwort; ohne gueltige Organisation ist nichts übernommen. */
export function viaOrganizationOf(body: Record<string, unknown>): ViaOrganization | undefined {
  const organization = ASRECORD(body.organization);
  if (organization === undefined || typeof organization.partyId !== 'string' || typeof organization.displayName !== 'string' || organization.displayName === '') {
    return undefined;
  }
  const postalAddress = addressOf(body.postalAddress);
  const emailAddress = valueOf(body.emailAddress);
  const phoneNumber = valueOf(body.phoneNumber);
  const weitere = organization.additionalEmploymentCount;
  return {
    organizationPartyId: organization.partyId,
    organizationName: organization.displayName,
    additionalEmploymentCount: typeof weitere === 'number' && Number.isSafeInteger(weitere) && weitere > 0 ? weitere : 0,
    ...(postalAddress === undefined ? {} : { postalAddress }),
    ...(emailAddress === undefined ? {} : { emailAddress }),
    ...(phoneNumber === undefined ? {} : { phoneNumber }),
  };
}

export async function fetchViaOrganization(options: {
  readonly baseUrl: string;
  readonly accessToken: string;
  readonly partyId: string;
  readonly send?: SendLike;
}): Promise<ViaOrganization | undefined> {
  try {
    const result = await readResource({
      url: `${options.baseUrl}/parties/${encodeURIComponent(options.partyId)}/via-organization`,
      accessToken: options.accessToken,
      ...(options.send === undefined ? {} : { send: options.send }),
    });
    return result.outcome === 'loaded' ? viaOrganizationOf(result.body) : undefined;
  } catch {
    return undefined;
  }
}

/** "und N weitere Organisationen", ausgeschrieben: sichtbar wie vorgelesen (Glossar: Kurzform nur in Spalten, Chips, Navigation). */
export function weitereOrganisationen(anzahl: number): string | undefined {
  if (anzahl <= 0) return undefined;
  return anzahl === 1 ? 'und 1 weitere Organisation' : `und ${anzahl} weitere Organisationen`;
}
